import { h as __h } from "preact";
import { Component, createRef } from "preact";
/**
 * @import {
 *   ComponentChildren,
 *   RefObject
 * } from "preact"
 */

import html from "~/util/html.js";
import Button from "./Button.js";
import ErrorBoundary from "./ErrorBoundary.js";
import Icon from "./Icon.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Modal.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Modal.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @typedef {object} Props
 * @property {string} [id]
 * @property {string | ComponentChildren | undefined} [title]
 * @property {ComponentChildren} [children]
 * @property {RefObject<Modal> | undefined} [ref]
 * @property {boolean | undefined} [open]
 * @property {boolean | undefined} [closable] Whether the user can dismiss via Esc, backdrop, or the
 *   X button. Default is `true`. Programmatic close (e.g. via ref) still works.
 * @property {((returnValue: string) => void) | undefined} [onClose]
 */

/** @extends {Component<Props>} */
export default class Modal extends Component {
  /** @type {RefObject<HTMLDialogElement>} */
  ref = createRef();

  /** @override */
  componentDidMount() {
    if (this.props.open) this.open();
  }

  /**
   * @param {Props} next
   * @override
   */
  componentWillReceiveProps(next) {
    if (next.open === this.props.open) return;
    if (next.open) this.open();else this.close();
  }
  open() {
    this.ref.current?.showModal();
  }
  close() {
    this.ref.current?.close();
  }

  /** @param {MouseEvent & { currentTarget: EventTarget & HTMLDialogElement }} e */
  #backdropDismiss = e => {
    if (this.props.closable === false) return;
    if ("closedBy" in HTMLDialogElement.prototype) return;
    const dialog = e.currentTarget;
    const rect = dialog.getBoundingClientRect();
    const overlapsDialogX = rect.left <= e.clientX && e.clientX <= rect.right;
    const overlapsDialogY = rect.top <= e.clientY && e.clientY <= rect.bottom;
    if (overlapsDialogX && overlapsDialogY) return;
    dialog.close();
  };
  render() {
    const closable = this.props.closable !== false;
    return __h("dialog", {
      id: this.props.id,
      ref: this.ref,
      "class": css.modal,
      closedby: closable ? "any" : "none",
      onClick: this.#backdropDismiss,
      onCancel: (/** @type {Event} */e) => {
        if (!closable) e.preventDefault();
      },
      onClose: (/** @type {CloseEvent & { currentTarget: HTMLDialogElement }} */e) => {
        const returnValue = e.currentTarget.returnValue;
        this.props.onClose?.(returnValue);

        // reset all forms within the modal
        for (const form of e.currentTarget.querySelectorAll("form")) {
          form.reset();
        }
      }
    }, this.props.title ? __h("header", {
      "class": css.header
    }, __h("h2", {
      "class": css.title
    }, this.props.title), closable && __h(Button, {
      level: "tertiary",
      size: "small",
      onClick: () => this.close()
    }, __h(Icon, {
      name: "ex",
      size: 12,
      label: "close"
    }))) : null, __h("div", {
      "class": css.content
    }, __h(ErrorBoundary, {}, " ", this.props.children, " ")));
  }
}